import type { GeoCatalogProvider } from "../interfaces/GeoCatalogProvider.js";
import type { GeoArQuery } from "../interfaces/GeoArAdapter.js";
import type { GeoArCandidate } from "../types/GeoArCandidate.js";
import type { GeoArResult } from "../types/GeoArResult.js";
import type { GeoArAccuracyWarning, GeoArStatus } from "../types/GeoArState.js";
import type { GeoFeatureType } from "../types/GeoFeature.js";
import { calculateBearing } from "./calculateBearing.js";
import { calculateConfidence } from "./calculateConfidence.js";
import { calculateDistance } from "./calculateDistance.js";
import { calculateElevationAngle } from "./calculateElevationAngle.js";
import { calculateRelativeAngle } from "./calculateRelativeAngle.js";
import { calculateRelativeVerticalAngle } from "./calculateRelativeVerticalAngle.js";
import { calculateVisibility } from "./calculateVisibility.js";

export interface GeoArCoreConfig {
  maxDistanceMeters: number;
  maxDistanceByFeatureType: Partial<Record<GeoFeatureType, number>>;
  gpsAccuracyThresholdMeters: number;
  headingAccuracyThresholdDegrees: number;
  alignmentThresholdDegrees: number;
}

const DEFAULT_CONFIG: GeoArCoreConfig = {
  maxDistanceMeters: 30_000,
  // Laboratory defaults only. They are configurable and must be calibrated in field tests.
  maxDistanceByFeatureType: {
    MOUNTAIN: 50_000,
    SUMMIT: 50_000,
    HILL: 25_000,
    LAGOON: 15_000,
    RIVER: 8_000,
    PLACE: 15_000,
    GEOGRAPHIC_POINT: 15_000,
  },
  gpsAccuracyThresholdMeters: 25,
  headingAccuracyThresholdDegrees: 20,
  alignmentThresholdDegrees: 3,
};

export class GeoArCore {
  private readonly config: GeoArCoreConfig;

  public constructor(
    private readonly catalogProvider: GeoCatalogProvider,
    config: Partial<GeoArCoreConfig> = {},
  ) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      maxDistanceByFeatureType: {
        ...DEFAULT_CONFIG.maxDistanceByFeatureType,
        ...(config.maxDistanceByFeatureType ?? {}),
      },
    };
    this.validateConfig();
  }

  public async query(input: GeoArQuery): Promise<GeoArResult[]> {
    this.validateQuery(input);

    const metadata = await this.catalogProvider.getMetadata();
    const offlineStatus: GeoArStatus | null = input.position.offline
      ? metadata?.offline_available
        ? "OFFLINE_AVAILABLE"
        : "OFFLINE_NOT_AVAILABLE"
      : null;

    if (input.position.offline && !metadata?.offline_available) {
      return [this.noInformationResult(true, ["OFFLINE_NOT_AVAILABLE"])];
    }

    const rawFeatures = await this.catalogProvider.getFeatures();
    const features = this.deduplicateByUuid(rawFeatures);

    if (features.length === 0) {
      return [
        this.noInformationResult(
          input.position.offline,
          offlineStatus ? [offlineStatus] : [],
        ),
      ];
    }

    const warnings = this.getAccuracyWarnings(input);
    const candidates = features.map<GeoArCandidate>((feature) => {
      const distance = calculateDistance(
        input.position.latitude,
        input.position.longitude,
        feature.latitude,
        feature.longitude,
      );
      const bearing = calculateBearing(
        input.position.latitude,
        input.position.longitude,
        feature.latitude,
        feature.longitude,
      );
      const relativeAngle =
        input.orientation.heading === null
          ? null
          : calculateRelativeAngle(bearing, input.orientation.heading);

      const canCalculateVertical =
        input.position.altitude !== null &&
        feature.altitude !== null;
      const elevationAngle = canCalculateVertical
        ? calculateElevationAngle(distance, input.position.altitude!, feature.altitude!)
        : null;
      const relativeVerticalAngle =
        elevationAngle !== null && input.orientation.pitch !== null
          ? calculateRelativeVerticalAngle(elevationAngle, input.orientation.pitch)
          : null;

      return {
        feature,
        distance,
        bearing,
        relative_angle: relativeAngle,
        elevation_angle: elevationAngle,
        relative_vertical_angle: relativeVerticalAngle,
      };
    });

    const results = candidates.map((candidate) =>
      this.toResult(candidate, input, warnings, offlineStatus),
    );

    this.markAmbiguousAlignments(results);
    this.sortResults(results);
    results.forEach((result, index) => {
      result.candidate_rank = result.feature_uuid === null ? null : index + 1;
    });

    return results;
  }

  private toResult(
    candidate: GeoArCandidate,
    input: GeoArQuery,
    warnings: GeoArAccuracyWarning[],
    offlineStatus: GeoArStatus | null,
  ): GeoArResult {
    const visibilityState = calculateVisibility({
      distance: candidate.distance,
      relativeAngle: candidate.relative_angle,
      fieldOfView: input.orientation.field_of_view,
      maxDistance: this.getMaxDistance(candidate.feature.feature_type),
    });

    const status: GeoArStatus[] = [visibilityState, ...warnings];
    if (offlineStatus) {
      status.push(offlineStatus);
    }

    return {
      feature_uuid: candidate.feature.feature_uuid,
      name: candidate.feature.name,
      feature_type: candidate.feature.feature_type,
      distance: candidate.distance,
      bearing: candidate.bearing,
      relative_angle: candidate.relative_angle,
      elevation_angle: candidate.elevation_angle,
      relative_vertical_angle: candidate.relative_vertical_angle,
      visibility_state: visibilityState,
      confidence: calculateConfidence({
        gpsAccuracy: input.position.accuracy,
        headingAccuracy: input.orientation.heading_accuracy,
        relativeAngle: candidate.relative_angle,
        fieldOfView: input.orientation.field_of_view,
        feature: candidate.feature,
        visibilityState,
        gpsAccuracyThreshold: this.config.gpsAccuracyThresholdMeters,
        headingAccuracyThreshold: this.config.headingAccuracyThresholdDegrees,
      }),
      candidate_rank: null,
      alignment_warning: false,
      accuracy_warning: [...warnings],
      data_source: candidate.feature.source,
      offline: input.position.offline,
      status,
    };
  }

  private markAmbiguousAlignments(results: GeoArResult[]): void {
    const visible = results.filter(
      (result) =>
        result.visibility_state === "VISIBLE" &&
        result.bearing !== null &&
        result.feature_uuid !== null,
    );

    for (let i = 0; i < visible.length; i += 1) {
      for (let j = i + 1; j < visible.length; j += 1) {
        const first = visible[i];
        const second = visible[j];
        if (!first || !second || first.bearing === null || second.bearing === null) {
          continue;
        }

        const angularDifference = Math.abs(
          calculateRelativeAngle(first.bearing, second.bearing),
        );
        if (angularDifference <= this.config.alignmentThresholdDegrees) {
          this.addAlignmentWarning(first);
          this.addAlignmentWarning(second);
        }
      }
    }
  }

  private addAlignmentWarning(result: GeoArResult): void {
    result.alignment_warning = true;
    if (!result.status.includes("AMBIGUOUS_ALIGNMENT")) {
      result.status.push("AMBIGUOUS_ALIGNMENT");
    }
  }

  private sortResults(results: GeoArResult[]): void {
    const visibilityPriority: Record<GeoArResult["visibility_state"], number> = {
      VISIBLE: 0,
      OUTSIDE_VIEW: 1,
      TOO_FAR: 2,
      NO_INFORMATION: 3,
    };

    results.sort((a, b) => {
      const stateDifference =
        visibilityPriority[a.visibility_state] - visibilityPriority[b.visibility_state];
      if (stateDifference !== 0) {
        return stateDifference;
      }

      if (b.confidence !== a.confidence) {
        return b.confidence - a.confidence;
      }

      const angleA = Math.abs(a.relative_angle ?? Number.POSITIVE_INFINITY);
      const angleB = Math.abs(b.relative_angle ?? Number.POSITIVE_INFINITY);
      if (angleA !== angleB) {
        return angleA - angleB;
      }

      return (a.distance ?? Number.POSITIVE_INFINITY) -
        (b.distance ?? Number.POSITIVE_INFINITY);
    });
  }

  private getMaxDistance(featureType: GeoFeatureType): number {
    return this.config.maxDistanceByFeatureType[featureType] ?? this.config.maxDistanceMeters;
  }

  private getAccuracyWarnings(input: GeoArQuery): GeoArAccuracyWarning[] {
    const warnings: GeoArAccuracyWarning[] = [];

    if (input.position.accuracy > this.config.gpsAccuracyThresholdMeters) {
      warnings.push("LOW_GPS_ACCURACY");
    }

    if (
      input.orientation.heading_accuracy === null ||
      input.orientation.heading_accuracy > this.config.headingAccuracyThresholdDegrees
    ) {
      warnings.push("LOW_HEADING_ACCURACY");
    }

    return warnings;
  }

  private noInformationResult(
    offline: boolean,
    extraStatus: GeoArStatus[],
  ): GeoArResult {
    return {
      feature_uuid: null,
      name: null,
      feature_type: null,
      distance: null,
      bearing: null,
      relative_angle: null,
      elevation_angle: null,
      relative_vertical_angle: null,
      visibility_state: "NO_INFORMATION",
      confidence: 0,
      candidate_rank: null,
      alignment_warning: false,
      accuracy_warning: [],
      data_source: null,
      offline,
      status: ["NO_INFORMATION", ...extraStatus],
    };
  }

  private deduplicateByUuid<T extends { feature_uuid: string }>(features: T[]): T[] {
    const seen = new Set<string>();
    return features.filter((feature) => {
      if (seen.has(feature.feature_uuid)) {
        return false;
      }
      seen.add(feature.feature_uuid);
      return true;
    });
  }

  private validateConfig(): void {
    const numericConfigValues = [
      this.config.maxDistanceMeters,
      this.config.gpsAccuracyThresholdMeters,
      this.config.headingAccuracyThresholdDegrees,
      this.config.alignmentThresholdDegrees,
    ];
    if (numericConfigValues.some((value) => !Number.isFinite(value) || value < 0)) {
      throw new RangeError("GeoArCoreConfig numeric values must be finite and non-negative.");
    }
    for (const value of Object.values(this.config.maxDistanceByFeatureType)) {
      if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
        throw new RangeError("maxDistanceByFeatureType values must be finite and non-negative.");
      }
    }
  }

  private validateQuery(input: GeoArQuery): void {
    const { latitude, longitude, altitude, accuracy } = input.position;
    const { heading, heading_accuracy, pitch, field_of_view } = input.orientation;

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      throw new RangeError("latitude must be a finite number between -90 and 90.");
    }
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      throw new RangeError("longitude must be a finite number between -180 and 180.");
    }
    if (altitude !== null && !Number.isFinite(altitude)) {
      throw new RangeError("altitude must be null or a finite number.");
    }
    if (!Number.isFinite(accuracy) || accuracy < 0) {
      throw new RangeError("accuracy must be a finite non-negative number.");
    }
    if (heading !== null && (!Number.isFinite(heading) || heading < 0 || heading >= 360)) {
      throw new RangeError("heading must be null or a finite number in [0, 360).");
    }
    if (
      heading_accuracy !== null &&
      (!Number.isFinite(heading_accuracy) || heading_accuracy < 0)
    ) {
      throw new RangeError("heading_accuracy must be null or a finite non-negative number.");
    }
    if (pitch !== null && (!Number.isFinite(pitch) || pitch < -90 || pitch > 90)) {
      throw new RangeError("pitch must be null or a finite number between -90 and 90.");
    }
    if (!Number.isFinite(field_of_view) || field_of_view <= 0 || field_of_view > 180) {
      throw new RangeError("field_of_view must be a finite number in (0, 180].");
    }
  }
}
